import { z } from "zod";

/** Spec 2a §4. `.strict()` everywhere (the package's security-boundary pattern). */
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD")
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return (
      Number.isFinite(parsed.getTime()) &&
      parsed.toISOString().slice(0, 10) === value
    );
  }, "Invalid calendar date");
const mealType = z.enum(["breakfast", "lunch", "dinner", "snack"]);
const coldLocation = z.enum(["fridge", "freezer"]);
const portions = z.number().int().positive();

export const createBatchForSlotSchema = z
  .object({
    portionsMade: portions,
    location: coldLocation,
    keepUntil: isoDate.optional(),
    useForSlots: z
      .array(z.object({ date: isoDate, mealType, servings: portions.optional() }).strict())
      .max(21)
      .optional(),
    confirmRescale: z.boolean().optional(),
  })
  .strict();
export type CreateBatchForSlotInput = z.infer<typeof createBatchForSlotSchema>;

export const updateBatchSchema = z
  .object({
    portionsMade: portions.optional(),
    location: coldLocation.optional(),
    keepUntil: isoDate.optional(),
    status: z.enum(["finished", "discarded"]).optional(),
    confirmRescale: z.boolean().optional(),
  })
  .strict()
  .refine((d) => d.portionsMade !== undefined || d.location !== undefined || d.keepUntil !== undefined || d.status !== undefined, {
    message: "At least one field is required",
  });
export type UpdateBatchInput = z.infer<typeof updateBatchSchema>;

export const createAdHocBatchSchema = z
  .object({ recipeId: z.string().min(1), proteinChoiceId: z.string().trim().min(1).max(100).nullable().optional(), madeOn: isoDate, location: coldLocation, portionsMade: portions, keepUntil: isoDate.optional() })
  .strict();
export type CreateAdHocBatchInput = z.infer<typeof createAdHocBatchSchema>;

export const batchEventSchema = z
  .object({ portions, outcome: z.enum(["eaten", "discarded"]), slotId: z.string().min(1).optional() })
  .strict();
export type BatchEventInput = z.infer<typeof batchEventSchema>;

export const createLeftoversSlotSchema = z
  .object({ date: isoDate, mealType, batchId: z.string().min(1), servings: portions.optional() })
  .strict();
export type CreateLeftoversSlotInput = z.infer<typeof createLeftoversSlotSchema>;

export const listBatchesQuerySchema = z
  .object({
    availableOn: isoDate.optional(),
    mealType: mealType.optional(),
    mealPlanId: z.string().min(1).optional(),
    needsOutcome: z.enum(["true", "false"]).optional().transform((v) => v === "true"),
  })
  .strict();
export type ListBatchesQuery = z.infer<typeof listBatchesQuerySchema>;
